// Route: /audio-recaps/new — 4-шаговый мастер аудио-резюме.
// Реальная mutation через api.audioRecaps.recaps.generate.
// См. docs/ui-spec/audio-recaps.new.{desktop,mobile}.md.
import { tolerantSearch } from "@studyhub/shared/utils/tolerant-search";
import {
	Button,
	cn,
	ElevatedPanel,
	NARROW_PAGE_SHELL,
	PANEL_PAD,
	SectionHeader,
	Separator,
} from "@studyhub/ui";
import { toast } from "@studyhub/ui/toast";
import { IconArrowLeft, IconArrowRight, IconHeadphones, IconSparkles } from "@tabler/icons-react";
import { createFileRoute, Link, Navigate, useNavigate, useSearch } from "@tanstack/react-router";
import { useEffect, useMemo, useRef } from "react";
import { useTranslation } from "react-i18next";
import { z } from "zod";
import { LengthSlider } from "@/components/audio/length-slider";
import { CustomBriefField } from "@/components/audio-recaps/custom-brief-field";
import { PresetPicker } from "@/components/audio-recaps/preset-picker";
import { WizardFormatGrid } from "@/components/audio-recaps/wizard-format-grid";
import { WizardSourceStep } from "@/components/audio-recaps/wizard-source-step";
import { WizardStepper } from "@/components/audio-recaps/wizard-stepper";
import { WizardVoicesStep } from "@/components/audio-recaps/wizard-voices-step";
import { useMaterialsList } from "@/components/materials/_api";
import { PagePendingFallback } from "@/components/shared/page-pending-fallback";
import { WizardStickyFooter } from "@/components/shared/wizard-sticky-footer";
import {
	type AudioRecapWizardState,
	buildGenerateInput,
	useAudioRecapWizard,
	useGenerateRecap,
} from "@/hooks/use-audio-recap-wizard";
import { useGenerationLocale } from "@/hooks/use-generation-locale";
import { markArtifactSeenKey } from "@/hooks/use-mark-artifact-seen";
import { useRequireOnline } from "@/hooks/use-require-online";
import { useTier } from "@/hooks/use-tier";
import { track } from "@/lib/observability/analytics";
import { api } from "@/lib/trpc-client";
import { userFacingErrorMessage } from "@/lib/user-facing-error";

const searchSchema = z.object({
	/** Prefill из workspace материала / create-from-chat. */
	materialId: z.string().uuid().optional(),
	/** Prefill из коллекции (XOR с materialId на UI). */
	setId: z.string().uuid().optional(),
});

export const Route = createFileRoute("/audio-recaps/new")({
	validateSearch: tolerantSearch(searchSchema),
	component: AudioRecapNewPage,
	pendingComponent: PagePendingFallback,
});

function AudioRecapNewPage(): React.ReactElement {
	const { t } = useTranslation("audio");
	const { online, guard } = useRequireOnline();
	const navigate = useNavigate();
	const { isFree } = useTier();
	const search = useSearch({ from: "/audio-recaps/new" });
	const { state, update, next, prev, goTo, setState } = useAudioRecapWizard(
		search.setId
			? { setId: search.setId, materialIds: [], materialId: null }
			: search.materialId
				? { materialId: search.materialId, materialIds: [search.materialId] }
				: undefined,
	);
	const materialsQuery = useMaterialsList();
	const generate = useGenerateRecap();
	const { mutate: setWatching } = api.notifications.setWatching.useMutation();

	const materials = useMemo(() => materialsQuery.data?.items ?? [], [materialsQuery.data]);
	const primaryMaterial = useMemo(
		() => materials.find((m) => m.id === state.materialIds[0]) ?? null,
		[materials, state.materialIds],
	);

	// Предзаполнение языка озвучки. Значение обязано совпадать с тем, что
	// подставил бы сервер (`resolveGenerationLocale`): «как язык интерфейса» по
	// умолчанию, фиксированный ISO или язык материала — если так выбрано в
	// /settings/language. Раньше здесь безусловно подставлялся язык материала,
	// мимо настройки аккаунта, и вдобавок без проверки значения: у материала с
	// неопределённым языком в колонке лежал `simple` (regconfig поиска, не язык)
	// — комбобокс показывал пустое место, каталог голосов запрашивался по «si»,
	// а «Сгенерировать» падало с 400 «invalid ISO 639-1 locale». Инцидент
	// 2026-08-21; данные вычищены миграцией 0142, а `isIsoLocale` внутри хука
	// оставлен: колонка так и осталась `TEXT`.
	const { resolve: resolveLocale, isLoading: localePrefLoading } = useGenerationLocale();
	const languageTouchedRef = useRef(false);
	useEffect(() => {
		if (languageTouchedRef.current || localePrefLoading) return;
		const next = resolveLocale(primaryMaterial?.language);
		if (next !== state.language) update("language", next);
	}, [resolveLocale, localePrefLoading, primaryMaterial, state.language, update]);

	const handleUpdate: typeof update = (key, value) => {
		if (key === "language") languageTouchedRef.current = true;
		update(key, value);
	};

	const canNext = canAdvance(state);

	useEffect(() => {
		window.scrollTo({ top: 0, left: 0, behavior: "auto" });
	}, [state.step]);

	if (isFree) {
		return <Navigate to="/settings/billing" replace />;
	}

	const handleSubmit = async () => {
		if (!guard()) return;
		if (!state.setId && state.materialIds.length === 0) {
			toast.error(t("wizard.errorNoMaterial"));
			return;
		}
		try {
			const input = buildGenerateInput(state);
			if (state.customBrief.trim().length > 0) {
				track("audio_recap.custom_brief_used", {
					preset: state.stylePreset,
					briefLength: state.customBrief.length,
				});
			}
			const result = await generate.mutateAsync(input);
			// Presence-aware: mute notify сразу после id, до navigate на $id
			// (гонка с быстрым Inngest / до монтирования useArtifactWatching).
			setWatching({
				groupKey: markArtifactSeenKey("audio-recap", result.recap.id),
				watching: true,
			});
			toast.success(t("wizard.queued"));
			await navigate({ to: "/audio-recaps/$id", params: { id: result.recap.id } });
		} catch (err) {
			toast.error(userFacingErrorMessage(err, t) ?? t("wizard.errorGeneric"));
		}
	};

	return (
		<div className={cn(NARROW_PAGE_SHELL, "space-y-section pb-24")}>
			<div className="space-y-2">
				<Button
					asChild
					variant="ghost"
					size="sm"
					className="-ml-2 cursor-pointer text-muted-foreground"
				>
					<Link to="/audio-recaps">
						<IconArrowLeft size={14} className="mr-1" />
						{t("list.backToList")}
					</Link>
				</Button>
				<SectionHeader
					icon={<IconHeadphones />}
					category="ai"
					title={t("wizard.title")}
					description={t("wizard.description")}
				/>
			</div>

			<WizardStepper current={state.step} onStepClick={goTo} />

			<ElevatedPanel className={PANEL_PAD}>
				{state.step === 1 && (
					<StepWrap label={t("wizard.step.source")} hint={t("wizard.step1Hint")}>
						<WizardSourceStep
							sourceTab={state.sourceTab}
							materialIds={state.materialIds}
							setId={state.setId}
							onSourceTabChange={(tab) => update("sourceTab", tab)}
							onScopeChange={(sel) => {
								if (sel.collectionId) {
									setState((prev) => ({
										...prev,
										setId: sel.collectionId,
										materialIds: [],
										materialId: null,
									}));
								} else {
									setState((prev) => ({
										...prev,
										setId: null,
										materialIds: sel.materialIds,
										materialId: sel.materialIds[0] ?? null,
									}));
								}
							}}
						/>
					</StepWrap>
				)}
				{state.step === 2 && (
					<StepWrap label={t("wizard.step.format")} hint={t("wizard.step2Hint")}>
						<Step2Format
							format={state.format}
							stylePreset={state.stylePreset}
							customBrief={state.customBrief}
							onFormatChange={(f, voiceModeDefault) => {
								update("format", f);
								update("voiceMode", voiceModeDefault);
							}}
							onStylePresetChange={(p) => update("stylePreset", p)}
							onCustomBriefChange={(b) => update("customBrief", b)}
						/>
					</StepWrap>
				)}
				{state.step === 3 && (
					<StepWrap label={t("wizard.step.voices")} hint={t("wizard.step3Hint")}>
						<WizardVoicesStep state={state} onUpdate={handleUpdate} />
					</StepWrap>
				)}
				{state.step === 4 && (
					<StepWrap label={t("wizard.step.length")} hint={t("wizard.step4Hint")}>
						<LengthSlider
							value={state.targetDurationSec}
							onChange={(sec) => update("targetDurationSec", sec)}
						/>
					</StepWrap>
				)}
			</ElevatedPanel>

			<WizardStickyFooter>
				<Button
					variant="outline"
					className="pointer-coarse:h-11"
					onClick={() => {
						if (state.step === 1) void navigate({ to: "/audio-recaps" });
						else prev();
					}}
				>
					<IconArrowLeft size={14} />
					{state.step === 1 ? t("wizard.cancel") : t("wizard.back")}
				</Button>
				{state.step < 4 ? (
					<Button onClick={next} disabled={!canNext} className="pointer-coarse:h-11">
						{t("wizard.next")}
						<IconArrowRight size={14} />
					</Button>
				) : (
					<Button
						onClick={() => void handleSubmit()}
						disabled={generate.isPending || !canNext || !online}
						className="pointer-coarse:h-11"
					>
						<IconSparkles size={14} />
						{generate.isPending ? t("wizard.submitting") : t("wizard.generate")}
					</Button>
				)}
			</WizardStickyFooter>
		</div>
	);
}

function StepWrap({
	label,
	hint,
	children,
}: {
	label: string;
	hint: string;
	children: React.ReactNode;
}): React.ReactElement {
	return (
		<div className="space-y-4 animate-enter">
			<div className="space-y-0.5">
				<h2 className="text-sm font-semibold text-foreground">{label}</h2>
				<p className="text-xs leading-snug text-muted-foreground">{hint}</p>
			</div>
			<Separator />
			{children}
		</div>
	);
}

interface Step2Props {
	format: AudioRecapWizardState["format"];
	stylePreset: AudioRecapWizardState["stylePreset"];
	customBrief: AudioRecapWizardState["customBrief"];
	onFormatChange: (
		format: AudioRecapWizardState["format"],
		voiceModeDefault: "single" | "multi",
	) => void;
	onStylePresetChange: (preset: NonNullable<AudioRecapWizardState["stylePreset"]>) => void;
	onCustomBriefChange: (value: string) => void;
}

function Step2Format({
	format,
	stylePreset,
	customBrief,
	onFormatChange,
	onStylePresetChange,
	onCustomBriefChange,
}: Step2Props): React.ReactElement {
	return (
		<div className="space-y-4">
			<WizardFormatGrid value={format} onChange={onFormatChange} />
			{format && (
				<>
					<Separator className="my-5" />
					<PresetPicker format={format} value={stylePreset} onChange={onStylePresetChange} />
					<CustomBriefField value={customBrief} onChange={onCustomBriefChange} />
				</>
			)}
		</div>
	);
}

function canAdvance(state: AudioRecapWizardState): boolean {
	switch (state.step) {
		case 1:
			return !!state.setId || state.materialIds.length >= 1;
		case 2:
			return !!state.format;
		case 3: {
			if (!state.voices[0]?.voiceId) return false;
			if (state.voiceMode === "multi" && !state.voices[1]?.voiceId) return false;
			return true;
		}
		case 4:
			return state.targetDurationSec >= 60;
		default:
			return false;
	}
}
